import { AwsUtil } from './../authentication/aws.service';
import {
    ContractualFlowData,
    ProjectionData,
    CorrectionData,
    ExplainData,
    ProjectionCorrectionData,
    ProjectionMonitoringData,
    GetSurvivalHorizonExplainRequest,
    GetSurvivalHorizonExplainOutput,
    WebApiEndpoints,
} from '@liquitics/transaction';
import { CurrencyUtil } from '@liquitics/transaction';
import { generateEventDatetime } from '@liquitics/transaction';

import { AppConfig } from './../app.config';
import { Observable } from 'rxjs';
import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { SnapshotService } from './../snapshot/snapshot.service';
import { environment } from './../../environments/environment';
import { AppEnvironment } from './../app-env';
import { CognitoUtil } from './../authentication/cognito.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseRestService } from '../service/base-rest.service';
import { ServiceRegistry } from '../service/service-registry.service';
import { productTypeDisplayName } from '../common/product-types';
import { Reducer } from '../common/reduce/reducer';
import * as moment from 'moment';

const EXPLAIN_QUERY_LIMIT = 500;

@Injectable()
export class ExplainService extends BaseRestService<ExplainData> {



    constructor(protected http: HttpClient,
        protected awsUtil: AwsUtil,
        protected serviceRegistry: ServiceRegistry,
        protected dashboardEventService: DashboardEventService,
        private snapshotService: SnapshotService,
        private appConfig: AppConfig) {
        super(http, awsUtil, serviceRegistry, dashboardEventService);
    }

    getAPIName(params?: any): string {
        return WebApiEndpoints.QuerySurvivalHorizonExplain;
    }

    useDemoDataFile(): boolean {
        return false;
    }


    getExplainData(currency: string, startDate: string, endDate: string,
        minEventDatetime?: moment.Moment, maxEventDatetime?: moment.Moment): Observable<ExplainData> {
        const request = new GetSurvivalHorizonExplainRequest();
        request.currency = currency;
        request.startDate = startDate;
        request.endDate = endDate;
        request.limit = EXPLAIN_QUERY_LIMIT;
        request.today = this.appConfig.getTodayString();
        if (this.appConfig.asOfDateMode) {
            const maxDatetimeStr = generateEventDatetime(this.appConfig.asOfDatetimeUTC);
            request.maxEventDatetime = maxDatetimeStr;
        } else {
            if (minEventDatetime) {
                request.minEventDatetime = generateEventDatetime(minEventDatetime.clone().utc());
            }
            if (maxEventDatetime) {
                request.maxEventDatetime = generateEventDatetime(maxEventDatetime.clone().utc());
            }
        }
        const requestAny: any = request;
        requestAny['experimentalFlags'] = 'newprojections';
        return this.getDataObservable(request);
    }

    protected unwrapAPIPayload(payload: any) {
        return payload.data;
    }

    postResolveData(explainData: ExplainData, params?: any): ExplainData {
        const currency = params['currency'];
        const scale = CurrencyUtil.currencyScalePower(currency);
        if (explainData) {
            if (explainData.contractualFlowData) {
                // const startingContractual = this.snapshotService.contractualFlowData(currency, params['startDate']);
                const startingContractual = [];
                const today = explainData.contractualFlowData;
                const groupedByDeskProduct = today
                    .map(d => {
                        return d;
                    })
                    .concat(startingContractual)
                    .map(d => {
                        d.flowAmount = CurrencyUtil.roundAndScale(d.flowAmount, 3, scale);
                        d.todayAmount = CurrencyUtil.roundAndScale(d.todayAmount, 3, scale);
                        return d;
                    })
                    .filter(d => {
                        return (d.flowAmount > 0.0 || d.flowAmount < 0.0);
                    })
                    .reduce((grouped, d: ContractualFlowData) => {
                        const key: string = d.desk + '.' + d.productType + '.' + d.direction;
                        const value: ContractualFlowData = grouped[key];
                        if (value) {
                            value.flowAmount += d.flowAmount;
                            value.todayAmount += d.todayAmount;
                        } else {
                            grouped[key] = d;
                        }
                        return grouped;
                    }, {});
                explainData.contractualFlowData = Object.keys(groupedByDeskProduct)
                    .map(key => {
                        const contractualFlow: ContractualFlowData = groupedByDeskProduct[key];
                        contractualFlow.productType = productTypeDisplayName(contractualFlow.productType);
                        return contractualFlow;
                    })
                    .sort((a, b) => {
                        return Math.abs(b.flowAmount) - Math.abs(a.flowAmount);
                    })
                    ;

            }
            if (explainData.unexpectedFlowData) {
                const today = explainData.unexpectedFlowData;
                const groupedByDeskProductClientGroup = today
                    .map(d => {
                        d.todayAmount = d.flowAmount;
                        d.flowAmount = CurrencyUtil.roundAndScale(d.flowAmount, 2, scale);
                        d.todayAmount = CurrencyUtil.roundAndScale(d.todayAmount, 2, scale);
                        return d;
                    })
                    .filter(d => {
                        return (d.flowAmount > 0.0 || d.flowAmount < 0.0);
                    })
                    .reduce((grouped, d: ContractualFlowData) => {
                        const key: string = d.desk + '.' + d.productType + '.' + d['targetName'] + '.' + d.direction;
                        const value: ContractualFlowData = grouped[key];
                        if (value) {
                            value.flowAmount += d.flowAmount;
                        } else {
                            grouped[key] = d;
                        }
                        return grouped;
                    }, {});
                explainData.unexpectedFlowData = Object.keys(groupedByDeskProductClientGroup)
                    .map(key => {
                        const contractualFlow: ContractualFlowData = groupedByDeskProductClientGroup[key];
                        contractualFlow.productType = productTypeDisplayName(contractualFlow.productType);
                        contractualFlow.clientGroup = contractualFlow['targetName'];
                        return contractualFlow;
                    })
                    .sort((a, b) => {
                        return Math.abs(b.flowAmount) - Math.abs(a.flowAmount);
                    })
                    ;

            }
            const netCorrectionData = [];
            if (explainData.projectionCorrectionData) {
                const projectionUpdateCorrections: CorrectionData[] = explainData.projectionCorrectionData
                    .map((d: ProjectionCorrectionData) => {
                        const correctionData = new CorrectionData();
                        correctionData.currency = d.currency;
                        correctionData.direction = d.direction;
                        correctionData.flowAmount = CurrencyUtil.roundAndScale(d.delta, 3, scale);
                        correctionData.reason = 'Projection(' + d.targetName + '/' + d.productType + ')';
                        return correctionData;
                    });
                netCorrectionData.push(...projectionUpdateCorrections);
            }
            if (explainData.correctionData) {
                const explainCorrections: CorrectionData[] = explainData.correctionData
                    .map((correctionData: CorrectionData) => {
                        correctionData.flowAmount = CurrencyUtil.roundAndScale(correctionData.flowAmount, 3, scale);
                        correctionData.reason = correctionData['type'] + '(' + correctionData.reason + ')';
                        return correctionData;
                    });
                netCorrectionData.push(...explainCorrections);
            }
            explainData.correctionData = netCorrectionData;

            // const interdeskFundingData = this.snapshotService.interdeskFundingData(currency, params['startDate']);
            if (explainData.interdeskFundingData) {
                explainData.interdeskFundingData = explainData.interdeskFundingData
                    .map(d => {
                        d.amount = CurrencyUtil.roundAndScale(d.amount, 3, scale);
                        d.productType = productTypeDisplayName(d.productType);
                        return d;
                    })
                    .sort((a, b) => {
                        return Math.abs(b.amount) - Math.abs(a.amount);
                    });
            }

            // const clientLevelFundingData = this.snapshotService.clientLevelFundingData(currency, params['startDate']);
            if (explainData.clientLevelFundingData) {
                explainData.clientLevelFundingData = explainData.clientLevelFundingData
                    .map(d => {
                        d.amount = CurrencyUtil.roundAndScale(d.amount, 3, scale);
                        d.productType = productTypeDisplayName(d.productType);
                        return d;
                    })
                    .sort((a, b) => {
                        return Math.abs(b.amount) - Math.abs(a.amount);
                    });
            }

            // const predictionData = this.snapshotService.predictionData(currency, params['startDate']);
            if (explainData.predictionData) {
                explainData.predictionData = explainData.predictionData
                    .map(d => {
                        d.amount = CurrencyUtil.roundAndScale(d.amount, 3, scale);
                        d.productType = productTypeDisplayName(d.productType);
                        return d;
                    })
                    .sort((a, b) => {
                        return Math.abs(b.amount) - Math.abs(a.amount);
                    });
            }

        }
        return explainData;
    }

    emptyAll(explainData: ExplainData) {
        explainData.clientLevelFundingData = [];
        explainData.contractualFlowData = [];
        explainData.unexpectedFlowData = [];
        explainData.correctionData = [];
        explainData.interdeskFundingData = [];
        explainData.predictionData = [];
        explainData.projectionCorrectionData = [];
    }

    invert(explainData: ExplainData) {
        if (explainData.clientLevelFundingData) {
            explainData.clientLevelFundingData.forEach(data => {
                data.amount = -data.amount;
            });
        }
        if (explainData.contractualFlowData) {
            explainData.contractualFlowData.forEach(data => {
                data.flowAmount = -data.flowAmount;
            });
        }
        if (explainData.unexpectedFlowData) {
            explainData.unexpectedFlowData.forEach(data => {
                data.flowAmount = -data.flowAmount;
            });
        }
        if (explainData.correctionData) {
            explainData.correctionData.forEach(data => {
                data.flowAmount = -data.flowAmount;
            });
        }
        if (explainData.interdeskFundingData) {
            explainData.interdeskFundingData.forEach(data => {
                data.amount = -data.amount;
            });
        }
        if (explainData.predictionData) {
            explainData.predictionData.forEach(data => {
                data.amount = -data.amount;
            });
        }
        if (explainData.projectionCorrectionData) {
            explainData.projectionCorrectionData.forEach(data => {
                data.delta = -data.delta;
            });
        }
    }

    concat(target: ExplainData, other: ExplainData): ExplainData {
        if (target.clientLevelFundingData) {
            if (other.clientLevelFundingData) {
                target.clientLevelFundingData = target.clientLevelFundingData.concat(other.clientLevelFundingData);
            }
        }
        if (target.contractualFlowData) {
            if (other.contractualFlowData) {
                target.contractualFlowData = target.contractualFlowData.concat(other.contractualFlowData);
            }
        }
        if (target.unexpectedFlowData) {
            if (other.unexpectedFlowData) {
                target.unexpectedFlowData = target.unexpectedFlowData.concat(other.unexpectedFlowData);
            }
        }
        if (target.correctionData) {
            if (other.correctionData) {
                target.correctionData = target.correctionData.concat(other.correctionData);
            }
        }
        if (target.interdeskFundingData) {
            if (other.interdeskFundingData) {
                target.interdeskFundingData = target.interdeskFundingData.concat(other.interdeskFundingData);
            }
        }
        if (target.predictionData) {
            if (other.predictionData) {
                target.predictionData = target.predictionData.concat(other.predictionData);
            }
        }
        if (target.projectionCorrectionData) {
            if (other.projectionCorrectionData) {
                target.projectionCorrectionData = target.projectionCorrectionData.concat(other.projectionCorrectionData);
            }
        }
        return target;
    }


    // Note, this method doesn't clone the data and it will changed the passed in data.
    // Make sure to pass in cloned data or data you do not care if modified.
    diffExplainData(liveExplainData: ExplainData, cobExplainData: ExplainData): ExplainData {
        const diff = new ExplainData();
        this.emptyAll(diff);
        this.invert(cobExplainData);
        this.concat(diff, liveExplainData)
        this.concat(diff, cobExplainData);
        const reducer = new Reducer();
        diff.contractualFlowData = reducer.reduce(diff.contractualFlowData,
            (data: ContractualFlowData) => { return data.currency + data.date + data.productType + data.desk; },
            (a, b) => {
                if (!a.flowAmount) {
                    a.flowAmount = 0.0;
                }
                if (!b.flowAmount) {
                    b.flowAmount = 0.0;
                }
                a.flowAmount += b.flowAmount;
                return a;
            },
            (data) => { return Object.assign(new ContractualFlowData(), data); }
        ).filter(data => {
            return (data.flowAmount !== 0.0);
        });
        diff.unexpectedFlowData = reducer.reduce(diff.unexpectedFlowData,
            (data: ContractualFlowData) => { return data.currency + data.date + data.productType + data.desk; },
            (a, b) => {
                if (!a.flowAmount) {
                    a.flowAmount = 0.0;
                }
                if (!b.flowAmount) {
                    b.flowAmount = 0.0;
                }
                a.flowAmount += b.flowAmount;
                return a;
            },
            (data) => { return Object.assign(new ContractualFlowData(), data); }
        ).filter(data => {
            return (data.flowAmount !== 0.0);
        });
        diff.correctionData = reducer.reduce(diff.correctionData,
            (data: CorrectionData) => { return data.currency + data.date + data.reason; },
            (a, b) => {
                if (!a.flowAmount) {
                    a.flowAmount = 0.0;
                }
                if (!b.flowAmount) {
                    b.flowAmount = 0.0;
                }
                a.flowAmount += b.flowAmount;
                return a;
            },
            (data) => { return Object.assign(new CorrectionData(), data); }
        ).filter(data => {
            return (data.flowAmount !== 0.0);
        });
        diff.clientLevelFundingData = reducer.reduce(diff.clientLevelFundingData,
            (data: ProjectionData) => { return data.currency + data.date + data.clientGroup; },
            (a, b) => {
                if (!a.amount) {
                    a.amount = 0.0;
                }
                if (!b.amount) {
                    b.amount = 0.0;
                }
                a.amount += b.amount;
                return a;
            },
            (data) => { return Object.assign(new ProjectionData(), data); }
        ).filter(data => {
            return (data.amount !== 0.0);
        });
        diff.interdeskFundingData = reducer.reduce(diff.interdeskFundingData,
            (data: ProjectionData) => { return data.currency + data.date + data.desk; },
            (a, b) => {
                if (!a.amount) {
                    a.amount = 0.0;
                }
                if (!b.amount) {
                    b.amount = 0.0;
                }
                a.amount += b.amount;
                return a;
            },
            (data) => { return Object.assign(new ProjectionData(), data); }
        ).filter(data => {
            return (data.amount !== 0.0);
        });
        diff.predictionData = reducer.reduce(diff.predictionData,
            (data: ProjectionData) => { return data.currency + data.date + data.clientGroup + data.desk; },
            (a, b) => {
                if (!a.amount) {
                    a.amount = 0.0;
                }
                if (!b.amount) {
                    b.amount = 0.0;
                }
                a.amount += b.amount;
                return a;
            },
            (data) => { return Object.assign(new ProjectionData(), data); }
        ).filter(data => {
            return (data.amount !== 0.0);
        });
        diff.projectionCorrectionData = reducer.reduce(diff.projectionCorrectionData,
            (data: ProjectionCorrectionData) => { return data.currency + data.date + data.direction + data.productType + data.targetName; },
            (a, b) => {
                if (!a.delta) {
                    a.delta = 0.0;
                }
                if (!b.delta) {
                    b.delta = 0.0;
                }
                a.delta += b.delta;

                return a;
            },
            (data) => { return Object.assign(new ProjectionCorrectionData(), data); }
        ).filter(data => {
            return (data.delta !== 0.0);
        });
        return diff;
    }

    emptyForUndefined(explainData: ExplainData) {
        if (!explainData.clientLevelFundingData) {
            explainData.clientLevelFundingData = [];
        }
        if (!explainData.contractualFlowData) {
            explainData.contractualFlowData = [];
        }
        if (!explainData.unexpectedFlowData) {
            explainData.unexpectedFlowData = [];
        }
        if (!explainData.correctionData) {
            explainData.correctionData = [];
        }
        if (!explainData.interdeskFundingData) {
            explainData.interdeskFundingData = [];
        }
        if (!explainData.predictionData) {
            explainData.predictionData = [];
        }
        if (!explainData.projectionCorrectionData) {
            explainData.projectionCorrectionData = [];
        }
    }
}




